import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Upload,
  FileText,
  X,
  Users,
  CheckCircle2,
  Clock,
  Sparkles,
  Settings2,
  GitBranch,
  AlertTriangle,
  BookOpen,
  Loader2,
} from "lucide-react";
import AppShell from "../AppShell";
import { api, API, getToken } from "../api";
import { useAuth } from "../AuthContext";
import { competencyCategories } from "../data/competencies";
import {
  PageHeader,
  Button,
  ErrorBanner,
  EmptyState,
} from "../../components/primitives";
const competencyCatalog = competencyCategories.flatMap((cat) =>
  cat.modules.map((m) => ({ id: m.id, name: m.name, category: cat.name }))
);
const competencyById = Object.fromEntries(
  competencyCatalog.map((c) => [c.id, c])
);

export default function Documents() {
  const { business } = useAuth();
  const [docs, setDocs] = useState([]);
  const [team, setTeam] = useState([]);
  const [error, setError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [manageFor, setManageFor] = useState(null);
  const [reviseFor, setReviseFor] = useState(null);
  const [generateFor, setGenerateFor] = useState(null);
  const [learningByDoc, setLearningByDoc] = useState(new Map());
  const isDemo = business?.is_demo;

  async function load() {
    try {
      const [d, t, lm] = await Promise.all([
        api.get("/documents"),
        api.get("/team"),
        api.get("/learning-modules").catch(() => []),
      ]);
      setDocs(d);
      setTeam(t);
      // Attach a lightweight learning status snapshot to each document so
      // the admin can see at a glance whether an SOP has an approved
      // module ready for staff assessment.
      const summary = new Map();
      for (const m of lm || []) {
        const key = m.document_id;
        if (!key) continue;
        const cur = summary.get(key) || { count: 0, approved: 0, draft: 0, rejected: 0, latest: null };
        cur.count += 1;
        cur[m.status] = (cur[m.status] || 0) + 1;
        if (!cur.latest || (m.generated_at || "") > (cur.latest.generated_at || "")) cur.latest = m;
        summary.set(key, cur);
      }
      setLearningByDoc(summary);
    } catch (e) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const rolesInUse = useMemo(
    () => Array.from(new Set(team.map((t) => t.role).filter(Boolean))).sort(),
    [team]
  );
  const deptsInUse = useMemo(
    () => Array.from(new Set(team.map((t) => t.department).filter(Boolean))).sort(),
    [team]
  );

  return (
    <AppShell>
      <PageHeader
        testid="documents-page"
        kicker="SOP & policy library"
        title="Documents & version control"
        description="Upload SOPs, link them to competencies and assign by role, department or individual. When a document is revised, everyone affected is automatically pushed back into re-acknowledgement and re-assessment."
        action={
          <Button
            variant="primary"
            icon={Upload}
            onClick={() => setShowUpload(true)}
            data-testid="docs-upload-btn"
          >
            Upload document
          </Button>
        }
      />

      {isDemo && (
        <div className="mb-5 rounded-lg border border-[#D4A65A]/30 bg-[#FDF6E7] px-4 py-3 text-[13px] text-[#8C6B33]">
          You're in demo mode — uploads, revisions and assignments are disabled.
          Create a workspace to bring in your real SOPs.
        </div>
      )}

      <ErrorBanner error={error} className="mb-5" />

      <div className="space-y-3" data-testid="docs-list">
        {docs.length === 0 && (
          <EmptyState
            icon={Upload}
            title="No documents yet"
            description="Upload your first SOP or policy. We'll extract the text, help you link it to a competency and get it ready for staff assignment."
            action={
              !isDemo && (
                <Button
                  variant="primary"
                  icon={Upload}
                  onClick={() => setShowUpload(true)}
                  data-testid="docs-empty-upload-btn"
                >
                  Upload document
                </Button>
              )
            }
          />
        )}
        {docs.map((d) => (
          <DocRow
            key={d.id}
            d={d}
            onAssign={() => setAssignFor(d)}
            onManage={() => setManageFor(d)}
            onRevise={() => setReviseFor(d)}
            onGenerate={() => setGenerateFor(d)}
            learning={learningByDoc.get(d.id)}
            isDemo={isDemo}
          />
        ))}
      </div>

      {showUpload && (
        <UploadModal
          disabled={isDemo}
          onClose={() => setShowUpload(false)}
          onDone={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}

      {assignFor && (
        <AssignModal
          doc={assignFor}
          team={team}
          rolesInUse={rolesInUse}
          deptsInUse={deptsInUse}
          onClose={() => setAssignFor(null)}
          onDone={() => {
            setAssignFor(null);
            load();
          }}
        />
      )}

      {manageFor && (
        <ManageDocumentDrawer
          doc={manageFor}
          rolesInUse={rolesInUse}
          deptsInUse={deptsInUse}
          onClose={() => setManageFor(null)}
          onSaved={() => {
            setManageFor(null);
            load();
          }}
        />
      )}

      {reviseFor && (
        <ReviseModal
          doc={reviseFor}
          onClose={() => setReviseFor(null)}
          onDone={() => {
            setReviseFor(null);
            load();
          }}
        />
      )}

      {generateFor && (
        <GenerateLearningModal
          doc={generateFor}
          onClose={() => setGenerateFor(null)}
          onDone={() => setGenerateFor(null)}
        />
      )}
    </AppShell>
  );
}

/* ---------------- doc row ---------------- */

function DocRow({ d, onAssign, onManage, onRevise, onGenerate, learning, isDemo }) {
  const superseded = !!d.superseded_by;
  const linked = d.competency_ids || [];
  const roles = d.role_scopes || [];
  const depts = d.department_scopes || [];
  const canGenerate = !superseded && !!d.storage_path && linked.length > 0;
  const learningApproved = (learning?.approved || 0) > 0;
  const learningDraft = (learning?.draft || 0) > 0;
  const learningLabel = learningApproved
    ? `Learning approved${learning.count > 1 ? ` · ${learning.approved}/${learning.count}` : ""}`
    : learningDraft
    ? `Learning drafted · needs review`
    : null;
  return (
    <div
      data-testid={`doc-row-${d.id}`}
      className={`rounded-xl border ${superseded ? "border-slate-200 bg-slate-50/60" : "border-slate-200 bg-white"} p-4 md:p-5 flex flex-col gap-4`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <span className="h-11 w-11 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
          <FileText size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-slate-900 text-[15px] font-semibold tracking-tight truncate">
              {d.title}
            </div>
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 bg-slate-100 rounded px-2 py-0.5">
              v{d.version}
            </span>
            {superseded && (
              <span
                data-testid={`doc-superseded-${d.id}`}
                className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5"
              >
                Superseded
              </span>
            )}
            {d.supersedes && (
              <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                Current
              </span>
            )}
            {learningLabel && (
              <Link
                to={
                  learning?.latest?.id
                    ? `/app/learning-review/${learning.latest.id}`
                    : "/app/learning-review"
                }
                data-testid={`doc-learning-pill-${d.id}`}
                title={
                  learningApproved
                    ? "An approved AI learning module is ready — click to preview"
                    : "AI learning has been drafted — review and approve in the Learning workspace"
                }
                className={`text-[10.5px] uppercase tracking-[0.14em] font-semibold border rounded px-2 py-0.5 inline-flex items-center gap-1 ${
                  learningApproved
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200 hover:border-emerald-400"
                    : "text-amber-700 bg-amber-50 border-amber-200 hover:border-amber-400"
                }`}
              >
                <BookOpen size={10} /> {learningLabel}
              </Link>
            )}
          </div>
          <div className="mt-1 text-[12.5px] text-slate-500 line-clamp-2">
            {d.description || d.original_filename}
          </div>
          <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />{" "}
              {new Date(d.created_at).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={11} /> {d.assigned_count} assigned
            </span>
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 size={11} /> {d.completed_count} completed
            </span>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 md:shrink-0">
          {d.storage_path && (
            <a
              href={`${API}/documents/${d.id}/download?token=${getToken()}`}
              onClick={(e) => downloadWithAuth(e, d.id, d.original_filename)}
              data-testid={`doc-view-${d.id}`}
              className="text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-2"
            >
              View PDF
            </a>
          )}
          <button
            onClick={onManage}
            disabled={isDemo || superseded}
            data-testid={`doc-manage-${d.id}`}
            title="Link competencies & set audience scope"
            className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
          >
            <Settings2 size={13} /> Manage
          </button>
          <button
            onClick={onRevise}
            disabled={isDemo || superseded}
            data-testid={`doc-revise-${d.id}`}
            title="Cut a new version and require re-acknowledgement"
            className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
          >
            <GitBranch size={13} /> Revise
          </button>
          <button
            onClick={onGenerate}
            disabled={isDemo || !canGenerate}
            data-testid={`doc-generate-learning-${d.id}`}
            title={
              !d.storage_path
                ? "Upload a PDF to generate learning content"
                : linked.length === 0
                ? "Link a competency first (Manage)"
                : "Draft AI learning + assessment questions from this SOP"
            }
            className="text-[12px] font-semibold text-[#8C6B33] hover:text-[#0B1220] border border-[#D4A65A]/40 hover:border-[#D4A65A] bg-[#FDF6E7] hover:bg-[#F7EBCE] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
          >
            <BookOpen size={13} /> Generate learning
          </button>
          <button
            onClick={onAssign}
            disabled={isDemo || superseded}
            data-testid={`doc-assign-${d.id}`}
            className="text-[12px] font-semibold bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] rounded-lg px-3 py-2"
          >
            Assign
          </button>
        </div>
      </div>

      {/* Metadata chips row */}
      {(linked.length > 0 || roles.length > 0 || depts.length > 0) && (
        <div
          className="flex flex-wrap gap-2 pt-3 border-t border-slate-100"
          data-testid={`doc-meta-${d.id}`}
        >
          {linked.map((cid) => (
            <span
              key={cid}
              className="inline-flex items-center gap-1 rounded-full bg-[#FDF6E7] border border-[#D4A65A]/40 text-[#8C6B33] text-[10.5px] font-semibold px-2 py-0.5"
              data-testid={`doc-comp-chip-${d.id}-${cid}`}
              title={competencyById[cid]?.name || cid}
            >
              <Sparkles size={10} /> {competencyById[cid]?.name || cid}
            </span>
          ))}
          {roles.map((r) => (
            <span
              key={`r-${r}`}
              className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-[10.5px] font-semibold px-2 py-0.5"
            >
              Role · {r}
            </span>
          ))}
          {depts.map((d0) => (
            <span
              key={`d-${d0}`}
              className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10.5px] font-semibold px-2 py-0.5"
            >
              Dept · {d0}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

async function downloadWithAuth(e, docId) {
  e.preventDefault();
  try {
    const res = await fetch(`${API}/documents/${docId}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    alert(err.message);
  }
}

/* ---------------- upload modal ---------------- */

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function UploadModal({ onClose, onDone, disabled }) {
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("1.0");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [competencyIds, setCompetencyIds] = useState(new Set());
  const [competencyQuery, setCompetencyQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  // Filter the competency catalog. `competencyCategories` is imported at top.
  const filteredCategories = useMemo(() => {
    const q = competencyQuery.trim().toLowerCase();
    if (!q) return competencyCategories;
    return competencyCategories
      .map((cat) => ({
        ...cat,
        modules: cat.modules.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            cat.name.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.modules.length > 0);
  }, [competencyQuery]);

  function toggleCompetency(id) {
    setCompetencyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onFileChosen(f) {
    setError("");
    if (!f) {
      setFile(null);
      return;
    }
    // Client-side PDF validation — mirrors the backend's 415 check but
    // surfaces the failure instantly instead of after an upload round-trip.
    const isPdf =
      f.type === "application/pdf" ||
      (f.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError(
        "Only PDF documents are supported. Please convert your file to PDF and try again."
      );
      if (inputRef.current) inputRef.current.value = "";
      setFile(null);
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("This file is over 10 MB. Please compress the PDF and try again.");
      if (inputRef.current) inputRef.current.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  }

  async function submit(e) {
    e.preventDefault();
    if (!file) {
      setError("Please choose a PDF");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("title", title || file.name);
      fd.append("version", version);
      fd.append("description", description);
      fd.append("competency_ids", Array.from(competencyIds).join(","));
      fd.append("file", file);
      await api.postForm("/documents", fd);
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-4 md:p-5 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 my-6 max-h-[calc(100vh-3rem)] overflow-y-auto overscroll-contain [scrollbar-width:thin]"
        onClick={(e) => e.stopPropagation()}
        data-testid="docs-upload-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Upload a document
            </h2>
            <p className="text-[13px] text-slate-500 mt-1">
              PDF only, up to 10 MB. Link competencies and set an audience
              in one step.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
              PDF file
            </span>
            <div
              className={`mt-1.5 rounded-lg border-2 transition-colors px-4 py-5 cursor-pointer ${
                file
                  ? "border-solid border-emerald-300 bg-emerald-50/70 hover:bg-emerald-50"
                  : "border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 text-center"
              }`}
              onClick={() => inputRef.current?.click()}
              data-testid="docs-upload-dropzone"
            >
              {file ? (
                <div className="flex items-center gap-3" data-testid="docs-upload-selected">
                  <span className="h-10 w-10 shrink-0 rounded-lg bg-white border border-emerald-200 flex items-center justify-center text-emerald-700">
                    <FileText size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-emerald-800">
                      <CheckCircle2 size={13} /> Document selected and ready to upload
                    </div>
                    <div className="mt-0.5 text-[13px] font-semibold text-slate-900 truncate">
                      {file.name}
                    </div>
                    <div className="text-[11.5px] text-slate-500">
                      PDF · {formatFileSize(file.size)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    data-testid="docs-upload-clear"
                    className="shrink-0 text-slate-400 hover:text-slate-700 rounded-md p-1"
                    aria-label="Remove selected file"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={18} className="mx-auto text-slate-400" />
                  <div className="mt-2 text-[13px] text-slate-600">
                    Click to choose a PDF
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    Only PDF documents are supported (max 10 MB)
                  </div>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => onFileChosen(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
          </label>

          <TextField
            label="Title"
            testid="docs-upload-title"
            value={title}
            onChange={setTitle}
            placeholder="Controlled Drug Handling SOP"
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Version"
              testid="docs-upload-version"
              value={version}
              onChange={setVersion}
              placeholder="1.0"
            />
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
              Description (optional)
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Short summary of what this document covers…"
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
            />
          </label>

          {/* Inline competency picker — replaces the previous two-step
              "Upload → Manage → Link" flow. Optional; skipping this is
              fine, an owner can still open Manage on the doc row later. */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                  Link competencies · {competencyIds.size} selected
                </div>
                <div className="mt-1 text-[12px] text-slate-500 leading-relaxed">
                  Anyone assigned a linked competency later will be
                  auto-pushed this document. Skip if you'd rather link
                  competencies via Manage after upload.
                </div>
              </div>
            </div>
            <input
              value={competencyQuery}
              onChange={(e) => setCompetencyQuery(e.target.value)}
              placeholder="Search competencies…"
              data-testid="docs-upload-competency-search"
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
            />
            <div className="mt-3 space-y-3 max-h-56 overflow-y-auto pr-1">
              {filteredCategories.length === 0 && (
                <div className="text-[12px] text-slate-500 text-center py-4">
                  No competencies match "{competencyQuery}".
                </div>
              )}
              {filteredCategories.map((cat) => (
                <div key={cat.id}>
                  <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
                    {cat.name}
                  </div>
                  <div className="grid gap-1">
                    {cat.modules.map((m) => {
                      const on = competencyIds.has(m.id);
                      return (
                        <label
                          key={m.id}
                          data-testid={`docs-upload-comp-${m.id}`}
                          className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] cursor-pointer border ${
                            on
                              ? "bg-[#FDF6E7] border-[#D4A65A]/40"
                              : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleCompetency(m.id)}
                            className="h-4 w-4 rounded border-slate-300 text-[#0B1220] focus:ring-[#D4A65A]/30"
                          />
                          <span
                            className={
                              on
                                ? "text-[#8C6B33] font-semibold"
                                : "text-slate-800"
                            }
                          >
                            {m.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <ErrorBanner error={error} />}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="accent"
              disabled={busy || disabled || !file}
              loading={busy}
              data-testid="docs-upload-submit"
            >
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- assign modal (extended with role/dept scopes) ---------------- */

function AssignModal({ doc, team, rolesInUse, deptsInUse, onClose, onDone }) {
  const [selected, setSelected] = useState({});
  const [roles, setRoles] = useState(new Set());
  const [depts, setDepts] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const ids = Object.keys(selected).filter((id) => selected[id]);
  const totalReached = new Set(ids);
  for (const m of team) {
    if (roles.has(m.role) || (m.department && depts.has(m.department))) {
      totalReached.add(m.id);
    }
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      await api.post(`/documents/${doc.id}/assign`, {
        team_member_ids: ids,
        role_scopes: Array.from(roles),
        department_scopes: Array.from(depts),
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function toggleSet(setter, value) {
    setter((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-5 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 my-6 max-h-[calc(100vh-3rem)] overflow-y-auto overscroll-contain [scrollbar-width:thin]"
        onClick={(e) => e.stopPropagation()}
        data-testid="docs-assign-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Assign document
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 truncate">
              {doc.title} · v{doc.version}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {(rolesInUse.length > 0 || deptsInUse.length > 0) && (
          <div className="mt-5 space-y-3">
            {rolesInUse.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
                  By role
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {rolesInUse.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleSet(setRoles, r)}
                      data-testid={`docs-assign-role-${r}`}
                      className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border ${
                        roles.has(r)
                          ? "bg-[#0B1220] text-white border-[#0B1220]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {deptsInUse.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
                  By department
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {deptsInUse.map((d0) => (
                    <button
                      key={d0}
                      type="button"
                      onClick={() => toggleSet(setDepts, d0)}
                      data-testid={`docs-assign-dept-${d0}`}
                      className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border ${
                        depts.has(d0)
                          ? "bg-emerald-700 text-white border-emerald-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {d0}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
            Individuals
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {team.length === 0 && (
              <div className="p-5 text-sm text-slate-500 text-center">
                Add team members first.
              </div>
            )}
            {team.map((m) => (
              <label
                key={m.id}
                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50"
                data-testid={`docs-assign-tm-${m.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[m.id]}
                  onChange={(e) =>
                    setSelected({ ...selected, [m.id]: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-slate-300 text-[#0B1220] focus:ring-[#D4A65A]/30"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">
                    {m.name}
                  </div>
                  <div className="text-[11.5px] text-slate-500 truncate">
                    {m.role}
                    {m.department ? ` · ${m.department}` : ""}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="text-[11.5px] text-slate-500">
            Reach: <b className="text-slate-900">{totalReached.size}</b> staff
            member{totalReached.size === 1 ? "" : "s"}
          </div>
          <button
            onClick={submit}
            disabled={busy || totalReached.size === 0}
            data-testid="docs-assign-submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] px-4 py-2.5 text-sm font-semibold"
          >
            {busy ? "Assigning…" : `Assign to ${totalReached.size}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- manage drawer ---------------- */

function ManageDocumentDrawer({ doc, rolesInUse, deptsInUse, onClose, onSaved }) {
  const [linked, setLinked] = useState(new Set(doc.competency_ids || []));
  const [roles, setRoles] = useState((doc.role_scopes || []).join(", "));
  const [depts, setDepts] = useState((doc.department_scopes || []).join(", "));
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return competencyCategories;
    return competencyCategories
      .map((cat) => ({
        ...cat,
        modules: cat.modules.filter((m) =>
          m.name.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.modules.length > 0);
  }, [query]);

  function toggle(id) {
    setLinked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      await api.patch(`/documents/${doc.id}`, {
        competency_ids: Array.from(linked),
        role_scopes: roles.split(",").map((r) => r.trim()).filter(Boolean),
        department_scopes: depts.split(",").map((d) => d.trim()).filter(Boolean),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-stretch justify-end"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white shadow-2xl w-full max-w-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="docs-manage-drawer"
      >
        <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
              Manage document
            </div>
            <h2 className="mt-1 font-display text-lg font-semibold text-slate-900 leading-tight truncate">
              {doc.title}
            </h2>
            <div className="mt-1 text-[12px] text-slate-500">
              v{doc.version} · {doc.assigned_count} assigned ·{" "}
              {doc.completed_count} completed
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
          {/* Audience scope */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
              Audience scope
            </div>
            <p className="text-[12.5px] text-slate-500 leading-relaxed mb-3">
              Anyone matching a role or department below is automatically
              assigned. Existing team members are updated on save; new joiners
              inherit the scope automatically when they match.
            </p>
            <label className="block mb-3">
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
                Roles (comma-separated)
              </span>
              <input
                value={roles}
                onChange={(e) => setRoles(e.target.value)}
                placeholder="e.g. Pharmacist, QA Manager"
                data-testid="docs-manage-roles"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
              />
              {rolesInUse.length > 0 && (
                <div className="mt-1.5 text-[10.5px] text-slate-500">
                  In use: {rolesInUse.join(", ")}
                </div>
              )}
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
                Departments (comma-separated)
              </span>
              <input
                value={depts}
                onChange={(e) => setDepts(e.target.value)}
                placeholder="e.g. Dispensary, Quality"
                data-testid="docs-manage-depts"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
              />
              {deptsInUse.length > 0 && (
                <div className="mt-1.5 text-[10.5px] text-slate-500">
                  In use: {deptsInUse.join(", ")}
                </div>
              )}
            </label>
          </section>

          {/* Linked competencies */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
              Linked competencies · {linked.size} selected
            </div>
            <p className="text-[12.5px] text-slate-500 leading-relaxed mb-3">
              When you assign one of these competencies to a staff member, this
              document is automatically pushed to them at the same time. When
              this document is revised, the linked competencies flip back to
              re-assessment for anyone previously certified.
            </p>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search competencies…"
              data-testid="docs-manage-competency-search"
              className="w-full mb-3 rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
            />
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {filtered.map((cat) => (
                <div key={cat.id}>
                  <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
                    {cat.name}
                  </div>
                  <div className="grid gap-1">
                    {cat.modules.map((m) => {
                      const on = linked.has(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] cursor-pointer border ${
                            on
                              ? "bg-[#FDF6E7] border-[#D4A65A]/40"
                              : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`docs-manage-comp-${m.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle(m.id)}
                            className="h-4 w-4 rounded border-slate-300 text-[#0B1220] focus:ring-[#D4A65A]/30"
                          />
                          <span
                            className={
                              on
                                ? "text-[#8C6B33] font-semibold"
                                : "text-slate-800"
                            }
                          >
                            {m.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {error && (
          <div className="mx-6 mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
            {error}
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-[12.5px] text-slate-500 hover:text-slate-800 px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="docs-manage-save"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-2 text-[13px] font-semibold"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- revise modal ---------------- */

function ReviseModal({ doc, onClose, onDone }) {
  const [version, setVersion] = useState(bumpVersion(doc.version));
  const [description, setDescription] = useState(doc.description || "");
  const [resetCompetencies, setResetCompetencies] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`/documents/${doc.id}/revise`, {
        new_version: version.trim(),
        description: description.trim() || undefined,
        reset_competencies: resetCompetencies,
      });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-4 md:p-5 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 my-auto max-h-[calc(100vh-3rem)] overflow-y-auto overscroll-contain [scrollbar-width:thin]"
        onClick={(e) => e.stopPropagation()}
        data-testid="docs-revise-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
              Revise document
            </div>
            <h2 className="mt-1 font-display text-lg font-semibold text-slate-900 leading-tight">
              {doc.title}
            </h2>
            <div className="text-[12px] text-slate-500 mt-1">
              Current version: v{doc.version}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        {!result && (
          <>
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-[12.5px] text-amber-800 flex gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                The current version will be marked as superseded. Every
                assignment against it that isn't already completed will move to
                <b> Superseded</b>. The same audience will be freshly assigned
                against the new version.
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <TextField
                label="New version"
                testid="docs-revise-version"
                value={version}
                onChange={setVersion}
                placeholder="e.g. 2.0"
                required
              />
            </div>
            <label className="block mt-4">
              <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                What changed (optional)
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Change summary — regulator drivers, sections revised…"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none"
              />
            </label>

            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={resetCompetencies}
                onChange={(e) => setResetCompetencies(e.target.checked)}
                data-testid="docs-revise-reset-comp"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0B1220] focus:ring-[#D4A65A]/30"
              />
              <span className="text-[12.5px] text-slate-700 leading-relaxed">
                Also reset any previously <b>completed</b> competency
                assignments linked to this document — the affected staff will
                need to be re-assessed against the new version.
              </span>
            </label>

            {error && (
              <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="text-[12.5px] text-slate-500 hover:text-slate-800 px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || !version.trim()}
                data-testid="docs-revise-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-2 text-[13px] font-semibold"
              >
                {busy ? "Cutting new version…" : "Confirm revision"}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="mt-5" data-testid="docs-revise-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-[12.5px] text-emerald-800 font-semibold">
                Revision complete
              </div>
              <ul className="mt-2 text-[12.5px] text-emerald-900 space-y-0.5">
                <li>
                  New version:{" "}
                  <b>v{result.new_document?.version}</b>
                </li>
                <li>
                  {result.superseded_assignments} assignment
                  {result.superseded_assignments === 1 ? "" : "s"} superseded
                </li>
                <li>
                  {result.new_assignments} fresh acknowledgement
                  {result.new_assignments === 1 ? "" : "s"} issued
                </li>
                {result.reset_competencies?.length > 0 && (
                  <li>
                    {result.reset_competencies.length} competency link
                    {result.reset_competencies.length === 1 ? "" : "s"} flipped
                    to re-assessment
                  </li>
                )}
              </ul>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={onDone}
                data-testid="docs-revise-done"
                className="inline-flex items-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220] px-4 py-2 text-[13px] font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function bumpVersion(v) {
  if (!v) return "2.0";
  const m = String(v).match(/^(\d+)(?:\.(\d+))?$/);
  if (!m) return `${v} rev`;
  const major = Number(m[1]);
  const minor = m[2] === undefined ? null : Number(m[2]);
  if (minor === null) return `${major + 1}.0`;
  return `${major}.${minor + 1}`;
}

/* ---------------- shared ---------------- */

function TextField({ label, testid, value, onChange, placeholder, required }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
        {label}
      </span>
      <input
        type="text"
        required={required}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
      />
    </label>
  );
}

/* ---------------- generate learning modal ---------------- */

function GenerateLearningModal({ doc, onClose, onDone }) {
  const linked = doc.competency_ids || [];
  const linkedList = linked.map(
    (id) => competencyById[id] || { id, name: id, category: "Unknown" }
  );
  const [competencyId, setCompetencyId] = useState(linkedList[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { learning_module, questions, message }
  const navigate = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (!competencyId) return;
    setBusy(true);
    setError("");
    try {
      // Snapshot existing module ids so we can detect the new one if the
      // ingress proxy 502s during the (~60s+) LLM call — the backend
      // continues generating and persists the module regardless.
      let existingIds = new Set();
      try {
        const existing = await api.get(
          `/learning-modules?competency_id=${encodeURIComponent(competencyId)}`
        );
        existingIds = new Set((existing || []).map((m) => m.id));
      } catch (_) {
        /* non-fatal */
      }

      let res;
      try {
        res = await api.post(`/documents/${doc.id}/generate-learning`, {
          competency_id: competencyId,
          competency_name:
            (competencyById[competencyId] || {}).name || undefined,
        });
      } catch (err) {
        // Ingress proxy 502/504 or network drop — poll for the module.
        const transient =
          err.status === 502 ||
          err.status === 504 ||
          err.status === undefined ||
          /Failed to fetch|NetworkError|timeout/i.test(err.message || "");
        if (!transient) throw err;
        res = await pollForNewModule(doc.id, competencyId, existingIds);
      }
      setSuccess(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function pollForNewModule(docId, compId, existingIds) {
    // Poll up to 3 minutes at 5s intervals.
    for (let i = 0; i < 36; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const modules = await api.get(
          `/learning-modules?competency_id=${encodeURIComponent(compId)}`
        );
        const newOne = (modules || []).find(
          (m) => m.document_id === docId && !existingIds.has(m.id)
        );
        if (newOne) {
          const detail = await api.get(`/learning-modules/${newOne.id}`);
          return {
            learning_module: detail.module,
            questions: detail.questions || [],
            message: `Generated ${(detail.questions || []).length} questions from ${newOne.document_title} v${newOne.document_version}.`,
          };
        }
      } catch (_) {
        /* keep polling */
      }
    }
    throw new Error(
      "Generation is taking longer than expected. Refresh the Learning page in a minute — the draft will appear once ready."
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-slate-200 my-auto"
        data-testid="generate-learning-modal"
      >
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8C6B33] font-semibold">
              AI learning · Claude Sonnet 4.6
            </div>
            <div className="mt-1 font-display text-lg tracking-tight text-slate-900 font-semibold">
              Generate learning from this SOP
            </div>
            <div className="mt-1 text-[12.5px] text-slate-500">
              {doc.title} <span className="text-slate-400">·</span> v{doc.version}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="generate-learning-close"
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {success ? (
            <div data-testid="generate-learning-success">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
                <b>Draft ready.</b> {success.message}
              </div>
              <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">
                The module and every generated question are marked <b>Draft</b>.
                Review each one against the source SOP before approving for
                staff assessment.
              </p>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={onDone}
                  className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
                >
                  Close
                </button>
                <button
                  onClick={() =>
                    navigate(`/app/learning-review/${success.learning_module.id}`)
                  }
                  data-testid="generate-learning-review-btn"
                  className="text-[12px] font-semibold bg-[#0B1220] hover:bg-[#131c31] text-white rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
                >
                  <BookOpen size={13} /> Review draft
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              {linkedList.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
                  Link at least one competency to this document (Manage) before
                  generating learning.
                </div>
              ) : (
                <>
                  <label className="block text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                    Draft for competency
                  </label>
                  <div className="mt-2 space-y-1.5">
                    {linkedList.map((c) => (
                      <label
                        key={c.id}
                        data-testid={`generate-learning-option-${c.id}`}
                        className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                          competencyId === c.id
                            ? "border-[#D4A65A] bg-[#FDF6E7]"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="competency"
                          value={c.id}
                          checked={competencyId === c.id}
                          onChange={() => setCompetencyId(c.id)}
                          className="mt-1"
                        />
                        <div className="min-w-0">
                          <div className="text-[13.5px] text-slate-900 font-medium">
                            {c.name}
                          </div>
                          <div className="text-[11.5px] text-slate-500">{c.category}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="mt-4 text-[12px] text-slate-500 leading-relaxed">
                    We'll extract the SOP text, then generate a 5–8 section
                    learning module plus 8 assessment questions (6 MCQ + 2
                    scenario). Each question includes a traceable quote back to
                    the source document. Generation typically takes 60–120
                    seconds — leave this window open and we'll keep polling
                    until the draft lands.
                  </p>
                </>
              )}

              {error && (
                <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
                  {error}
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !competencyId || linkedList.length === 0}
                  data-testid="generate-learning-submit"
                  className="text-[12px] font-semibold bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220] disabled:opacity-40 rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
                >
                  {busy ? (
                    <>
                      <Loader2 size={13} className="animate-spin" /> Drafting…
                    </>
                  ) : (
                    <>
                      <Sparkles size={13} /> Generate draft
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

